import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateSecretRequestSchema,
  type CreateSecretRequest,
  type SecretDetail,
  SecretEnvironmentSchema,
  SecretKindSchema,
  type SecretListItem,
  SecretProviderTypeSchema,
  UpdateSecretRequestSchema,
  type UpdateSecretRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzSelect,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateSecret, useUpdateSecret } from '../use-secrets';

interface SecretFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  secret?: SecretDetail | SecretListItem | null;
}

const KIND_LABELS: Record<string, string> = {
  API_KEY: 'API key',
  CREDENTIAL: 'Credential',
  TOKEN: 'Token',
  WEBHOOK_SECRET: 'Webhook secret',
  ENV_VAR: 'Environment variable',
  OTHER: 'Other',
};
const PROVIDER_TYPE_LABELS: Record<string, string> = {
  FLIGHTS: 'Flights',
  HOTELS: 'Hotels',
  TOURS: 'Tours',
  ACTIVITIES: 'Activities',
  PAYMENT: 'Payment',
  MESSAGING: 'Messaging',
  OTHER: 'Other',
};
const ENV_LABELS: Record<string, string> = { TEST: 'Test', LIVE: 'Live', ALL: 'All' };

const KIND_OPTIONS = SecretKindSchema.options.map((v) => ({
  value: v,
  label: KIND_LABELS[v] ?? v,
}));
// Radix Select forbids an empty-string item value, so the "none" choice uses a
// sentinel that we map to `null` on the way in/out.
const NO_PROVIDER_TYPE = '__none__';
const PROVIDER_TYPE_OPTIONS = [
  { value: NO_PROVIDER_TYPE, label: 'None' },
  ...SecretProviderTypeSchema.options.map((v) => ({
    value: v,
    label: PROVIDER_TYPE_LABELS[v] ?? v,
  })),
];
const ENV_OPTIONS = SecretEnvironmentSchema.options.map((v) => ({
  value: v,
  label: ENV_LABELS[v] ?? v,
}));

const CREATE_DEFAULTS: CreateSecretRequest = {
  key: '',
  label: '',
  description: '',
  kind: 'API_KEY',
  provider: '',
  providerType: null,
  environment: 'ALL',
  isActive: true,
  value: '',
};

// Value visibility policy: the field is plaintext while the operator
// is typing (both create and edit) so typos like "secure=993" can't
// slip through. The stored value is never echoed back here - edit mode
// shows an empty field with "Leave blank to keep current value" - so
// rendering type=text only exposes the operator's own input for the
// duration of the drawer, never a previously-saved secret.
const SecretFields = ({
  pending,
  isCreate,
}: {
  pending: boolean;
  isCreate: boolean;
}): React.JSX.Element => (
  <>
    <UzFormField name="key">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value.toLowerCase())}
          label="Key"
          placeholder="e.g. tbo.api.password"
          hint="Stable machine key: lowercase letters, numbers, dots, underscores, hyphens."
          error={error}
          required
          disabled={pending || !isCreate}
        />
      )}
    </UzFormField>
    <UzFormField name="label">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Label"
          placeholder="e.g. TBO API password"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="value">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          // Empty stays `undefined` so a blank field always means "keep the
          // stored value" (create still requires it; edit treats blank as keep).
          onChange={(e) => field.onChange(e.target.value === '' ? undefined : e.target.value)}
          type="text"
          autoComplete="new-password"
          label={isCreate ? 'Value' : 'New value'}
          placeholder={isCreate ? 'The secret value' : 'Leave blank to keep current value'}
          error={error}
          required={isCreate}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="provider">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Provider"
          placeholder="e.g. TBO, HotelBeds"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="providerType">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Provider type"
          options={PROVIDER_TYPE_OPTIONS}
          value={(field.value as string | null) ?? NO_PROVIDER_TYPE}
          onValueChange={(v) => field.onChange(v === NO_PROVIDER_TYPE ? null : v)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="kind">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Kind"
          options={KIND_OPTIONS}
          value={(field.value as string) || undefined}
          onValueChange={field.onChange}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="environment">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Environment"
          options={ENV_OPTIONS}
          value={(field.value as string) || undefined}
          onValueChange={field.onChange}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="description">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Description"
          placeholder="Optional"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive secrets are not served to provider clients."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const SecretFormDrawer = ({
  open,
  onOpenChange,
  mode,
  secret,
}: SecretFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createSecret = useCreateSecret();
  const updateSecret = useUpdateSecret();
  const pending = createSecret.isPending || updateSecret.isPending;

  const createForm = useForm<CreateSecretRequest>({
    resolver: zodResolver(CreateSecretRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateSecretRequest>({
    resolver: zodResolver(UpdateSecretRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (secret) {
      // The value is never prefilled - blank means "keep the stored secret".
      updateForm.reset({
        key: secret.key,
        label: secret.label,
        description: secret.description ?? '',
        kind: secret.kind,
        provider: secret.provider ?? '',
        providerType: secret.providerType,
        environment: secret.environment,
        isActive: secret.isActive,
        value: undefined,
      });
    }
  }, [open, isCreate, secret, createForm, updateForm]);

  const normalize = <T extends { provider?: string | null; description?: string | null }>(
    v: T,
  ): T => ({
    ...v,
    provider: v.provider?.trim() ? v.provider : null,
    description: v.description?.trim() ? v.description : null,
  });

  const onCreate = createForm.handleSubmit(async (values) => {
    await createSecret.mutateAsync(normalize(values));
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!secret) return;
    const payload = normalize(values);
    // Drop an empty value so the server keeps the existing ciphertext.
    if (!payload.value) delete payload.value;
    await updateSecret.mutateAsync({ id: secret.id, payload });
    onOpenChange(false);
  });

  const fields = <SecretFields pending={pending} isCreate={isCreate} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add secret' : `Edit ${secret?.key ?? 'secret'}`}
      description="Credentials are encrypted at rest (AES-256-GCM). The stored value is never shown here; use Reveal to view it."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
